import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { childmap, keymap, lccstate, pathmap, userstate, contentTrack, createNlsMiddleware, recommended } from 'lcc-state';
import { Icon } from '@holmescorp/base-widgets';
import { ScoreChart } from '@holmescorp/progress-chart';
import { publish } from '@holmescorp/pub-sub/main';
import { ScoreIcon } from './index';
import MicroCardButton from './card-button';
import css from '../css/where-you-left-off.m.css';
import progressCss from '../css/progress-view.m.css';
const factory = create({
    userstate,
    lccstate,
    theme,
    contentTrack,
    recommended,
    nls: createNlsMiddleware('ditabook')
}).properties();
export const OverallProgress = factory(({ middleware: { userstate, lccstate, theme, contentTrack, nls, recommended } }) => {
    const { messages: { assess_progress_threshold_high = '80', overall_progress_reading_weight = '0.75', overall_progress_quiz_weight = '0.25', overall_progress_header = 'Overall Progress', overall_progress_chart_label = 'Course Completion', has_pretest = 'false', micro_pretest_label = 'Take Pre-test' } } = nls();
    const themeCss = theme.classes(css);
    const themeProgressCss = theme.classes(progressCss);
    const { get, path } = userstate;
    const { get: lget, path: lpath } = lccstate;
    const passingScore = parseInt(assess_progress_threshold_high) || 80;
    const readingWeight = parseInt(overall_progress_reading_weight) || 0.75;
    const quizWeight = parseInt(overall_progress_quiz_weight) || 0.25;
    const status = get(path('status'));
    const { tracked } = contentTrack;
    const roots = lget(lpath('ditaroots'));
    const tgfkeys = roots.flatMap((root) => childmap.get(root) || []);
    const topicsets = tgfkeys.map((tgf) => {
        const { path } = keymap.get(tgf);
        const { topic } = pathmap.get(path);
        const { data } = status[tgf];
        const children = topic.topicset.children?.filter((topicgroup) => !('shortdesc' in topicgroup)) || [];
        return { ...topic.topicset.attributes, children, percent: data.percent };
    });
    const topicsetData = topicsets.reduce((acc, topicset) => {
        const topicdata = status[topicset.key || ''];
        const total = topicdata.data.total || 0;
        const completed = topicdata.data.complete || 0;
        const remainingReadingTime = topicset.children
            .map((tg) => {
            const { data: { readtime = 0, complete = 0, total = 0 } } = status[tg.topicgroup.attributes?.key || ''];
            return complete < total ? readtime : 0;
        })
            .reduce((acc, topicgroup) => {
            return acc + topicgroup;
        }, 0);
        const passed = topicdata.data.percent * 100 >= passingScore;
        return {
            readtime: acc.readtime + remainingReadingTime,
            total: acc.total + total,
            completed: acc.completed + completed,
            passed: acc.passed + (passed ? 1 : 0)
        };
    }, { readtime: 0, total: 0, completed: 0, passed: 0 });
    const weightedReadingScore = (topicsetData.completed / topicsetData.total) * readingWeight;
    const weightedQuizScore = (topicsetData.passed / topicsets.length) * quizWeight;
    const percentComplete = weightedReadingScore + weightedQuizScore;
    const scoreValue = Math.round(percentComplete * 100);
    const remainingModules = tracked.topicset_count - tracked.completed_count;
    const remainingHours = Math.round(topicsetData.readtime / 60);
    const hoursLabel = remainingHours === 1 ? 'hour' : 'hours';
    const hasPretest = has_pretest === 'true';
    const hasCompletedPretest = recommended.assessmentsComplete();
    return v('div', { classes: [themeCss.tailRoot] }, [
        v('h1', { classes: [themeCss.tgHeader], xqa: 'overall-progress-header' }, [overall_progress_header]),
        v('div', { classes: [themeCss.tgDetails], xqa: 'overall-progress-details' }, [
            w(ScoreChart, {
                score: scoreValue,
                useScoreColor: false,
                insideLabelText: overall_progress_chart_label,
                additionalClasses: [themeProgressCss.small],
                icon: w(ScoreIcon, { score: scoreValue })
            }),
            v('div', { classes: [css.actionContainer] }, [
                v('ul', { xqa: 'overall-progress-info' }, [
                    v('li', { xqa: 'overall-progress-modules-remaining' }, [
                        w(Icon, { faname: 'arrow-right' }),
                        `You have ${remainingModules} modules left to complete`
                    ]),
                    v('li', { xqa: 'overall-progress-hours-remaining' }, [
                        w(Icon, { faname: 'arrow-right' }),
                        `You have ${remainingHours} ${hoursLabel} of reading time remaining`
                    ])
                ]),
                hasPretest && !hasCompletedPretest
                    ? v('div', { classes: [css.actions], xqa: 'overall-progress-pretest-button' }, [
                        w(MicroCardButton, {
                            labelText: micro_pretest_label,
                            darkMode: true,
                            onClick: () => {
                                publish(`site/assessment`, 'pre-test');
                            }
                        })
                    ])
                    : null
            ])
        ])
    ]);
});
export default OverallProgress;
